import { useState } from 'react';
import { Plus, Trash2, ArrowRight, Waypoints } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import { Button, Card, CardBody, Badge, IconButton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, Alert, useToast } from '@vnetwork/ui/feedback';
import { useLoadBalancerForwards, useUpdateLoadBalancerForwards, type LoadBalancer } from '@/api/opennebula';
import { QuickForwardDialog } from './QuickForwardDialog';

/**
 * L4 port forwarding on the cluster's ingress load balancer, served by the appliance's
 * LVS service (keepalived + IPVS) — a data path entirely separate from HAProxy, so a
 * forward never touches the controller-managed ingress listeners (80/443). Exposes a
 * public TCP/UDP port on the LB that forwards to a nodePort on the worker nodes.
 */
export function IngressPortForwarding({
  serviceId,
  clusterId,
  computeSvc,
  lb,
}: {
  serviceId: string;
  clusterId: string;
  computeSvc: string;
  lb: LoadBalancer;
}) {
  const { show } = useToast();
  const { data: forwards = [] } = useLoadBalancerForwards(computeSvc, lb.id);
  const [adding, setAdding] = useState(false);
  const [delIndex, setDelIndex] = useState<number | null>(null);

  const save = useUpdateLoadBalancerForwards(computeSvc, lb.id, {
    onSuccess: () => { show({ tone: 'success', title: 'Forward removed' }); setDelIndex(null); },
    onError: () => show({ tone: 'danger', title: 'Remove failed' }),
  });

  const onDelete = (index: number) => save.mutate(forwards.filter((_, i) => i !== index));

  return (
    <Section
      title="Port forwarding"
      description="Expose a public TCP/UDP port on the ingress load balancer that forwards to a nodePort on the worker nodes."
      actions={
        <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setAdding(true)}>
          Quick forward port
        </Button>
      }
    >
      <Alert tone="info" title="Ad-hoc forwards" style={{ marginBottom: 'var(--vn-space-4)' }}>
        These L4 forwards run on a separate load-balancing path from the ingress controller's own listeners (80/443) —
        adding or removing one never affects ingress traffic. Use them to reach a nodePort directly (e.g. UDP DNS).
      </Alert>

      {forwards.length === 0 ? (
        <Card><CardBody><EmptyState icon={<Waypoints size={28} />} title="No forwards" description="Add a quick forward to reach a nodePort through the load balancer." /></CardBody></Card>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {forwards.map((f, i) => {
            const count = f.servers?.length ?? 0;
            return (
              <Card key={i} style={{ overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-3) var(--vn-space-4)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
                    <Badge tone="neutral" appearance="subtle">{f.protocol}</Badge>
                    <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' as React.CSSProperties['fontWeight'], fontFamily: 'var(--vn-font-mono)' }}>:{f.port}</span>
                    <ArrowRight size={14} style={{ color: 'var(--vn-fg-subtle)', flexShrink: 0 }} />
                    <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', whiteSpace: 'nowrap' }}>
                      {count} node{count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <IconButton variant="ghost" size="sm" aria-label="Remove forward" onClick={() => setDelIndex(i)}><Trash2 size={15} /></IconButton>
                </div>

                <div style={{ borderTop: '1px solid var(--vn-border-subtle)', background: 'var(--vn-bg-subtle)', padding: '6px var(--vn-space-4)' }}>
                  {count === 0 ? (
                    <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>No backend nodes.</span>
                  ) : (
                    <div style={{ display: 'grid' }}>
                      {f.servers!.map((s, si) => (
                        <div key={si} style={{ padding: '6px 0', borderTop: si ? '1px solid var(--vn-border-subtle)' : 'none', fontSize: 'var(--vn-font-size-sm)' }}>
                          <code style={{ fontFamily: 'var(--vn-font-mono)' }}>{s.host}:{s.port}</code>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {adding && (
        <QuickForwardDialog
          serviceId={serviceId}
          clusterId={clusterId}
          computeSvc={computeSvc}
          lbId={lb.id}
          existing={forwards}
          onClose={() => setAdding(false)}
        />
      )}
      <ConfirmDialog
        open={delIndex != null}
        onOpenChange={(o) => !o && setDelIndex(null)}
        title="Remove forward?"
        description="Traffic on this port will stop reaching the worker nodes."
        confirmLabel="Remove"
        destructive
        loading={save.isPending}
        onConfirm={() => delIndex != null && onDelete(delIndex)}
      />
    </Section>
  );
}
